import { type ComponentType, lazy } from "react";
import { isUpdateReloadPending, onUpdateReloadCleared } from "./update-reload";

/**
 * Session record of which route chunks have already spent their one reload.
 *
 * Keyed per chunk rather than as a single flag. A single flag was retired by
 * any clean route load, so a healthy parent or sibling route could clear it
 * while a stale child chunk kept failing — arm, reload, parent loads, clear,
 * child fails, arm again: a loop the guard existed to prevent. An entry here is
 * never cleared by a sibling's success, so each chunk gets exactly one reload
 * per session no matter what else loads around it.
 *
 * Nothing needs to clear the record for a later deploy either: chunk URLs are
 * content-hashed, so a new build asks for identities that were never recorded
 * and gets its own recovery.
 */
export const ROUTE_CHUNK_RELOAD_GUARD_KEY = "xan.route-chunk.reload-guard";

/**
 * Cap on recorded identities, so a long session on a persistently broken deploy
 * cannot grow the entry without bound. Oldest entries drop first; a chunk
 * evicted this way is one that already failed long enough ago to be worth one
 * more attempt.
 */
const MAX_RECORDED_CHUNKS = 32;

/** Identity for a chunk with no URL in its message (see `chunkIdentity`). */
const UNKNOWN_CHUNK = "unknown";

/**
 * The chunk URL a module-load failure message carries: absolute from the
 * browser, or root-relative (`/assets/x.css`) from Vite's stylesheet preload.
 */
const CHUNK_URL_PATTERN = /https?:\/\/\S+|\/\S+\.(?:m?js|css)\b/;

/**
 * Browser messages for a dynamic `import()` whose chunk URL no longer resolves.
 *
 * Each engine words it differently, and a CDN that answers a removed chunk with
 * the SPA's `index.html` trips the MIME-type variant. Vite also preloads a
 * route's stylesheets before the `import()` runs, and a removed one rejects with
 * its own wording. That rejection reaches the `load()` caller because Vite
 * rethrows it unless a `vite:preloadError` listener calls `preventDefault()`, so
 * this guard needs a signature for it rather than a second listener. Matching is
 * kept specific to module loading so an ordinary component render error still
 * reaches the ErrorBoundary rather than triggering a reload.
 */
const MODULE_LOAD_ERROR_SIGNATURES = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "failed to load module script",
  "unable to preload css for",
] as const;

function isModuleLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return MODULE_LOAD_ERROR_SIGNATURES.some((signature) => message.includes(signature));
}

function getReloadGuardStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * The failing chunk's identity, taken from the URL browsers put in the message.
 *
 * The filename carries the content hash, so it changes with every deploy — which
 * is what lets a recorded identity stay recorded without ever blocking a later
 * build's recovery. Messages that carry no URL ("Importing a module script
 * failed.") all collapse onto one identity, which is the same single-reload
 * bound the guard had before.
 */
function chunkIdentity(error: unknown): string {
  if (!(error instanceof Error)) {
    return UNKNOWN_CHUNK;
  }
  const url = error.message.match(CHUNK_URL_PATTERN)?.[0];
  if (!url) {
    return UNKNOWN_CHUNK;
  }
  try {
    return new URL(url, "http://origin.invalid").pathname;
  } catch {
    return url;
  }
}

function readRecordedChunks(storage: Storage): string[] {
  try {
    const raw = storage.getItem(ROUTE_CHUNK_RELOAD_GUARD_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    // Unreadable or corrupt storage: report nothing recorded but let the write
    // in `recordChunkReload` decide whether a reload is safe. A write that also
    // fails means the record cannot survive the reload, and the caller falls
    // through to the ErrorBoundary rather than risking a loop.
    return [];
  }
}

/**
 * Records that `identity` has spent its reload. Returns whether the record will
 * survive the reload — a `false` means the caller must not reload, because an
 * unrecorded reload is an unbounded one.
 */
function recordChunkReload(storage: Storage, identity: string): boolean {
  const recorded = readRecordedChunks(storage);
  const next = [...recorded, identity].slice(-MAX_RECORDED_CHUNKS);
  try {
    storage.setItem(ROUTE_CHUNK_RELOAD_GUARD_KEY, JSON.stringify(next));
    return true;
  } catch {
    // Quota exhausted, or storage that reads but refuses writes (Safari private
    // browsing). Nothing would remember this reload, so the next failure would
    // reload again, forever.
    return false;
  }
}

function defaultReload(): void {
  if (typeof window !== "undefined" && typeof window.location?.reload === "function") {
    window.location.reload();
  }
}

/**
 * Whether a failed route load is a stale chunk this session can still recover.
 *
 * True only for a module-load error backed by storage that has not already
 * recorded a reload for this chunk. Narrows `storage` to non-null so the caller
 * can record the reload.
 */
function canRecoverStaleChunk(error: unknown, storage: Storage | null): storage is Storage {
  if (!storage) {
    return false;
  }
  if (!isModuleLoadError(error)) {
    return false;
  }
  return !readRecordedChunks(storage).includes(chunkIdentity(error));
}

/**
 * A promise that never settles. After a reload has started, it holds React's
 * Suspense fallback until the document is replaced, instead of rendering
 * without the requested module.
 */
function untilDocumentReloads<T>(): Promise<T> {
  return new Promise<T>(() => {
    // Intentionally pending until the document reloads.
  });
}

/**
 * Recovers a stale route chunk with one reload, or rethrows for React to catch.
 *
 * The reload is recorded before it happens, so the reloaded build gets exactly
 * one attempt at this chunk: a still-failing chunk — a genuine outage or a
 * broken deploy rather than a stale tab — rethrows to the ErrorBoundary instead
 * of looping. If the record cannot be written it rethrows too, since a reload
 * nothing remembers is a reload nothing bounds.
 *
 * While an accepted update is reloading the tab, a chunk that fails to load is
 * the old build losing its precache to the new worker. That reload already
 * brings the new build, so recovery neither records nor reloads, and the page
 * reloads once in total. If the update ends without a reload (the user stayed,
 * or the accepted worker was discarded), the failure goes through normal
 * recovery then, so the route never waits on a reload that is not coming.
 */
function recoverFromRouteLoadError<T>(error: unknown, reload: () => void): Promise<T> {
  if (isModuleLoadError(error) && isUpdateReloadPending()) {
    return new Promise<T>((resolve, reject) => {
      onUpdateReloadCleared(() => {
        try {
          resolve(recoverFromRouteLoadError<T>(error, reload));
        } catch (recoveryError) {
          reject(recoveryError);
        }
      });
    });
  }
  const storage = getReloadGuardStorage();
  if (!canRecoverStaleChunk(error, storage)) {
    throw error;
  }
  if (!recordChunkReload(storage, chunkIdentity(error))) {
    throw error;
  }
  reload();
  return untilDocumentReloads<T>();
}

/**
 * Loads a route module, recovering a stale deploy chunk with one full reload.
 *
 * Route components load through content-hashed dynamic `import()` chunks. A
 * Cloudflare Pages deploy replaces every hash, so a tab opened against the
 * previous build asks for a chunk URL the new deploy has already removed and
 * the import rejects with "Failed to fetch dynamically imported module". React
 * cannot render the route and the ErrorBoundary shows the raw failure instead
 * of the page the operator navigated to.
 *
 * A full reload fetches the new deploy's HTML and its fresh chunk manifest, so
 * the retry resolves against URLs that exist. {@link recoverFromRouteLoadError}
 * bounds the recovery to one reload per chunk per session — a clean load of
 * some other route never returns a failing chunk's reload to it.
 *
 * `reload` is injectable only so the guard logic is testable without navigating
 * the test runner; production always uses the real page reload.
 */
export async function importRouteModule<T>(
  load: () => Promise<T>,
  reload: () => void = defaultReload,
): Promise<T> {
  try {
    return await load();
  } catch (error) {
    return await recoverFromRouteLoadError<T>(error, reload);
  }
}

/**
 * `React.lazy` for a route component, hardened against stale deploy chunks.
 *
 * `load` resolves the component itself; the `{ default }` envelope `React.lazy`
 * expects is built here so call sites read as "load this page". Every route
 * flows through this one seam, so the stale-chunk recovery in
 * {@link importRouteModule} covers the whole route tree without touching each
 * route.
 */
export function lazyRoute<T extends ComponentType<unknown>>(load: () => Promise<T>) {
  return lazy(() => importRouteModule(async () => ({ default: await load() })));
}
